// react
import { useEffect, useState } from 'react';

// router
import { Outlet, Route, Routes } from 'react-router-dom';

// pages
import { PageContracts } from './components/pages/Contracts/Contracts';
import { PageContract } from './components/pages/Contract/Contract';
import { PageDashboard } from './components/pages/Dashboard/Dashboard';
import { PageError } from './components/pages/Error/Error';
import { PageLogin } from './components/pages/Login/Login';
import { PageProfile } from './components/pages/Profile/Profile';

// layouts
import { Providers } from './components/layouts/Providers/Providers';

// types
import { Document, Entity, User } from './@types';

// hooks
import { useFetch } from './hooks/useFetch';

// context
import { ContextBackStack, ContextBanner, ContextDocsUser, ContextUser } from './App.context';

// styles
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// ui
import { BannerProps } from './components/ui/Banner/Banner';

// theme
import { theme } from './theme';

export const App = () => {
	// session
	const token = sessionStorage.getItem('token');
	const sessionUser = sessionStorage.getItem('user');
	const userDefault = sessionUser ? JSON.parse(sessionUser) : null;

	// state
	const [banner, setBanner] = useState<BannerProps | null>(null);
	const [docsUser, setDocsUser] = useState<Document[]>([]);
	const [pathnames, setPathnames] = useState<string[]>([]);
	const [user, setUser] = useState<User | null>(userDefault);

	// vars
	const hasBeneficiary =
		(user?.entities as Entity[])?.some((e) => e.type === 'BENEFICIARY') ?? false;
	const hasSupplier = (user?.entities as Entity[])?.some((e) => e.type === 'SUPPLIER') ?? false;

	console.log('ENV', process.env);

	const {
		fetchRequest: fetchUserDocs,
		isLoading: isLoadingDocsUser,
		refetch: refetchDocsUser,
	} = useFetch({
		url: `${process.env.REACT_APP_API_URL}/api/proxies/documents?user=${user?.id}&category=IDENTIFICATION,LICENSE`,
		isLazy: true,
		options: {
			headers: {
				Authorization: `Bearer ${token}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocsUser(res.data);
			}
		},
	});

	// fetch user blobs when token and user are set
	useEffect(() => {
		if (token && user) {
			fetchUserDocs();
		}
	}, [fetchUserDocs, token, user]);

	return (
		<ContextUser.Provider value={{ hasBeneficiary, hasSupplier, user, setUser }}>
			<ContextBanner.Provider value={{ banner, setBanner }}>
				<ContextDocsUser.Provider
					value={{ docsUser, isLoadingDocsUser, refetchDocsUser, setDocsUser }}>
					<ContextBackStack.Provider value={{ pathnames, setPathnames }}>
						<ThemeProvider theme={theme}>
							<CssBaseline />
							<Routes>
								{/* login */}
								<Route path="/" element={<PageLogin />} />
								<Route path="/login/email" element={<PageLogin loginType="email" />} />
								<Route path="/login/sms" element={<PageLogin loginType="sms" />} />
								<Route path="/error" element={<PageError />} />
								{/* authenticated pages */}
								<Route
									element={
										<Providers>
											<Outlet />
										</Providers>
									}>
									<Route path="/contracts" element={<PageContracts />} />
									<Route path="/contracts/:id" element={<PageContract />} />
									<Route path="/dashboard" element={<PageDashboard />} />
									<Route path="/profile" element={<PageProfile />} />
									<Route path="*" element={<PageError />} />
								</Route>
							</Routes>
						</ThemeProvider>
					</ContextBackStack.Provider>
				</ContextDocsUser.Provider>
			</ContextBanner.Provider>
		</ContextUser.Provider>
	);
};
